import { useContext, useEffect, useState } from "react";
import { CombinedContext } from "../../../context/combinedContext";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import translations from "../../../translations/translations";
import companyBasedRequest from "../../../request/companyBasedRequest";
import ExclusionModal from "../exclusionModal/exclusionModal";
import BaseScheduleModal from "../baseScheduleModal/baseScheduleModal";
import scheduleBasedRequest from "../../../request/scheduleBasedRequest";
import { ChevronLeft, ChevronRight, Edit, MoreVertical, Phone, User2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import CalendarSkeleton from "../calendarSkeleton/calendarSkeleton";
import MiniCalendarSkeleton from "../miniCalendarSkeleton/miniCalendarSkeleton";
import WalkinModal from "../walkinModal/walkinModal";
import BannerMessage from "../../common/bannerMessage/bannerMessage";
import AddBookingPopup from "../addBookingPopup/addBookingPopup";
import Confetti from "../../common/confetti/confetti";

dayjs.extend(isoWeek);

const StaffCalendar = () => {
	const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

	const today = dayjs();
	const todayIndex = today.isoWeekday() - 1; // 0 = Monday, 6 = Sunday
	const todayDay = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][todayIndex];

	const [view, setView] = useState("day");
	const [selectedDay, setSelectedDay] = useState(todayDay);
	const [initialLoading, setInitialLoading] = useState(true);
	const [weekLoading, setWeekLoading] = useState(false);
	const [events, setEvents] = useState([]);
	const [showPopup, setShowPopup] = useState(false);
	const [showDetails, setShowDetails] = useState(false);
	const [selectedEvent, setSelectedEvent] = useState(null);
	const [saving, setSaving] = useState(false);
	const [dayIndex, setDayIndex] = useState(todayIndex);
	const [activeModal, setActiveModal] = useState("");
	const [services, setServices] = useState(null);
	const [weekData, setWeekData] = useState(null);
	const [showMenu, setShowMenu] = useState(false);
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [calendarMonth, setCalendarMonth] = useState(dayjs());
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);
	const [editingTransit, setEditingTransit] = useState(false);
	const [transitValue, setTransitValue] = useState("");
	const [transitSaving, setTransitSaving] = useState(false);
	const [quickSlot, setQuickSlot] = useState(null);
	const [showConfetti, setShowConfetti] = useState(false);
	const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

	// Week navigation
	const [weekStart, setWeekStart] = useState(dayjs().startOf("isoWeek"));
	const weekEnd = dayjs(weekStart).add(6, "day");

	const [popupData, setPopupData] = useState({
		day: "",
		start: "",
		end: "",
		title: "",
		desc: "",
	});

	const [breaks, setBreaks] = useState([{ day: "Mon", start: "12:00", end: "13:00" }]);

	const [baseSchedule, setBaseSchedule] = useState(
		days.map((d) => ({
			day: d,
			open: ["Mon", "Tue", "Wed", "Thu", "Fri"].includes(d),
			start: "09:00",
			end: "17:00",
		})),
	);

	const { userData, language } = useContext(CombinedContext);

	const role = userData?.user?.role;

	const location = useLocation();

	const navigate = useNavigate();

	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);

		setTimeout(() => {
			setExiting(true);
		}, duration - 250);

		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		if (params.get("showLiveBannerStaff")) {
			setShowConfetti(true);
			setTimeout(() => setShowConfetti(false), 5000);
		}
	}, []);

	useEffect(() => {
		if (!location.state?.openModal) return;
		setActiveModal(location.state.openModal);
	}, [location.state, navigate]);

	useEffect(() => {
		setSelectedDay(days[dayIndex]);
	}, [dayIndex]);

	useEffect(() => {
		if (!userData?.user) return;

		async function loadInitial() {
			try {
				setInitialLoading(true);

				const startStr = weekStart.format("YYYY-MM-DD");
				const shopWeek = await companyBasedRequest.getStaffWeekSchedule(userData.user.scheduleId, startStr, userData.cognitoTokens.AccessToken);
				const services = await companyBasedRequest.getShopServices(userData.user.shopId, userData.cognitoTokens.AccessToken);
				setServices(services.services);
				// build events
				const allEvents = [];
				Object.entries(shopWeek.week || {}).forEach(([dateStr, dayObj]) => {
					(dayObj.bookings || []).forEach((b) => {
						allEvents.push({
							date: dateStr,
							start: dayjs(b.start).format("HH:mm"),
							end: dayjs(b.end).format("HH:mm"),
							clientName: b.clientName,
							service: b.service || [],
						});
					});
				});

				setEvents(allEvents);
				setWeekData(shopWeek);
				setInitialLoading(false);
			} catch (err) {
				console.error(err);
				showBanner("error", translations[language].calendar.errors?.loadWeek || "Failed to load calendar");
				setInitialLoading(false);
			}
		}

		loadInitial();
	}, [userData]);

	useEffect(() => {
		if (initialLoading) return;

		async function loadWeek() {
			try {
				setWeekLoading(true);

				const startStr = weekStart.format("YYYY-MM-DD");
				const shopWeek = await companyBasedRequest.getStaffWeekSchedule(userData.user.scheduleId, startStr, userData.cognitoTokens.AccessToken);

				// rebuild events
				const allEvents = [];
				Object.entries(shopWeek.week || {}).forEach(([dateStr, dayObj]) => {
					(dayObj.bookings || []).forEach((b) => {
						allEvents.push({
							date: dateStr,
							start: dayjs(b.start).format("HH:mm"),
							end: dayjs(b.end).format("HH:mm"),
							clientName: b.clientName,
							service: b.service || [],
						});
					});
				});

				setEvents(allEvents);
				setWeekData(shopWeek);
				setWeekLoading(false);
			} catch (err) {
				console.error(err);
				showBanner("error", translations[language].calendar.errors?.loadWeek || "Failed to load week");
				setWeekLoading(false);
			}
		}

		loadWeek();
	}, [weekStart]);

	const reloadWeek = async () => {
		try {
			const startStr = weekStart.format("YYYY-MM-DD");
			const shopWeek = await companyBasedRequest.getStaffWeekSchedule(userData.user.scheduleId, startStr, userData.cognitoTokens.AccessToken);

			const allEvents = [];
			Object.entries(shopWeek.week || {}).forEach(([dateStr, dayObj]) => {
				(dayObj.bookings || []).forEach((b) => {
					allEvents.push({
						date: dateStr,
						start: dayjs(b.start).format("HH:mm"),
						end: dayjs(b.end).format("HH:mm"),
						clientName: b.clientName,
						service: b.service || [],
					});
				});
			});

			setEvents(allEvents);
			setWeekData(shopWeek);
		} catch (err) {
			console.error("Failed to reload week", err);
			showBanner("error", translations[language].calendar.errors?.reload || "Failed to refresh calendar");
		}
	};

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (!e.target.closest(".sf-menu-wrapper")) setShowMenu(false);
		};
		document.addEventListener("click", handleClickOutside);
		return () => document.removeEventListener("click", handleClickOutside);
	}, []);

	// Compute dynamic hours based on longest shift across all staff
	const computeHours = (weekObj) => {
		if (!weekObj) return Array.from({ length: 10 }, (_, i) => 9 + i);

		let earliest = 24;
		let latest = 0;

		Object.values(weekObj).forEach((day) => {
			(day.shifts || []).forEach((s) => {
				const [sh] = s.start.split(":").map(Number);
				const [eh] = s.end.split(":").map(Number);
				if (sh < earliest) earliest = sh;
				if (eh > latest) latest = eh;
			});
		});

		if (earliest >= latest) return Array.from({ length: 10 }, (_, i) => 9 + i);

		return Array.from({ length: latest - earliest }, (_, i) => earliest + i);
	};

	const hours = computeHours(weekData?.week || []);

	const addPersonalEvent = async () => {
		const { date, start, end, title, desc } = popupData;

		if (!date || !start || !end) {
			return;
		}

		try {
			setSaving(true);

			await companyBasedRequest.addScheduleExclusion(userData.user.scheduleId, {
				startDate: date,
				endDate: date,
				reason: title || "Unavailable",
				blocks: [{ start, end }],
			});

			setShowPopup(false);
			setSaving(false);

			const startStr = weekStart.format("YYYY-MM-DD");
			const weekData = await companyBasedRequest.getStaffWeekSchedule(userData.user.scheduleId, startStr, userData.cognitoTokens.AccessToken);
		} catch (err) {
			console.error(err);
			showBanner("error", translations[language].calendar.errors?.saveBlock || "Failed to save block");
			setSaving(false);
		}
	};

	const handleEventClick = (event) => {
		setSelectedEvent(event);
		setTransitValue(event.transitMinutes ?? "");
		setEditingTransit(false);
		setShowDetails(true);
		setPopupData(event);
	};

	const handleThisWeek = () => {
		const start = dayjs().startOf("isoWeek");
		setWeekStart(start);
		setDayIndex(dayjs().diff(start, "day")); // 0..6
	};

	const getMinutes = (t) => {
		const date = dayjs(t); // automatically parses ISO
		return date.hour() * 60 + date.minute();
	};

	const hourHeight = 140;
	const visibleDays = view === "day" ? [selectedDay] : days;

	const selectedDateStr = dayjs(weekStart).add(dayIndex, "day").format("YYYY-MM-DD");

	const handleChooseDate = (dateString) => {
		const chosen = dayjs(dateString);
		const newWeek = chosen.startOf("isoWeek");
		const newIndex = chosen.diff(newWeek, "day");

		setWeekStart(newWeek);
		setDayIndex(newIndex);
		setSelectedDay(days[newIndex]);
		setShowDatePicker(false);
	};

	const saveTransitTime = async () => {
		if (!selectedEvent?.bookingId) return;

		const minutes = Number(transitValue);
		if (Number.isNaN(minutes) || minutes < 0) return;

		try {
			setTransitSaving(true);

			await companyBasedRequest.updateBookingTransitAdmin(selectedEvent.bookingId, minutes, userData.cognitoTokens.AccessToken);

			await reloadWeek();

			setSelectedEvent((prev) => (prev ? { ...prev, transitMinutes: minutes } : prev));

			setEditingTransit(false);

			showBanner("success", language === "es" ? "Tiempo de traslado actualizado" : "Transit time updated");
		} catch (err) {
			console.error(err);
			showBanner("error", language === "es" ? "Conflicto al actualizar el traslado" : "Transit time conflicts with another booking");
		} finally {
			setTransitSaving(false);
		}
	};

	const buildInactiveRangesStaff = (dayData, hours) => {
		const ranges = [];
		if (!dayData || !hours.length) return ranges;

		const globalStart = hours[0];
		const globalEnd = hours[hours.length - 1] + 1;

		let shiftStart = Infinity;
		let shiftEnd = -Infinity;

		(dayData.shifts || []).forEach((s) => {
			const [sh] = s.start.split(":").map(Number);
			const [eh] = s.end.split(":").map(Number);
			shiftStart = Math.min(shiftStart, sh);
			shiftEnd = Math.max(shiftEnd, eh);
		});

		if (!isFinite(shiftStart) || !isFinite(shiftEnd)) {
			return ranges;
		}

		// 1️⃣ Outside shift
		if (shiftStart > globalStart) {
			ranges.push({
				startHour: globalStart,
				endHour: shiftStart,
				type: "outsideShift",
			});
		}

		if (shiftEnd < globalEnd) {
			ranges.push({
				startHour: shiftEnd,
				endHour: globalEnd,
				type: "outsideShift",
			});
		}

		// 2️⃣ Blocks / exclusions
		(dayData.blocks || []).forEach((block) => {
			const [sh, sm] = block.start.split(":").map(Number);
			const [eh, em] = block.end.split(":").map(Number);

			let startHour = sh + sm / 60;
			let endHour = eh + em / 60;

			if (endHour <= shiftStart || startHour >= shiftEnd) return;

			startHour = Math.max(startHour, shiftStart);
			endHour = Math.min(endHour, shiftEnd);

			ranges.push({
				startHour,
				endHour,
				type: "exclusion",
				blockData: block,
			});
		});

		(dayData.breaks || []).forEach((br) => {
			const [sh, sm] = br.start.split(":").map(Number);
			const [eh, em] = br.end.split(":").map(Number);

			let startHour = sh + sm / 60;
			let endHour = eh + em / 60;

			if (endHour <= shiftStart || startHour >= shiftEnd) return;

			startHour = Math.max(startHour, shiftStart);
			endHour = Math.min(endHour, shiftEnd);

			ranges.push({
				startHour,
				endHour,
				type: "break",
				breakData: br,
			});
		});

		// 3️⃣ Transit buffers
		(dayData.bookings || []).forEach((b) => {
			if (!b.isHomeService) return;

			const transit = Number(b.transitMinutes || 0);
			if (!transit || transit <= 0) return;

			const bookingStart = dayjs(b.start);
			const transitStart = bookingStart.subtract(transit, "minute");

			const startHour = transitStart.hour() + transitStart.minute() / 60;
			const endHour = bookingStart.hour() + bookingStart.minute() / 60;

			let adjustedStart = startHour;
			let adjustedEnd = endHour;

			if (adjustedEnd <= shiftStart || adjustedStart >= shiftEnd) return;

			adjustedStart = Math.max(adjustedStart, shiftStart);
			adjustedEnd = Math.min(adjustedEnd, shiftEnd);

			ranges.push({
				startHour: adjustedStart,
				endHour: adjustedEnd,
				type: "transit",
				bookingId: b.bookingId,
			});
		});

		return ranges;
	};

	const getValidServicesForSlot = (dayStr, startTime) => {
		if (!weekData?.week?.[dayStr]) return [];

		const dayData = weekData.week[dayStr];
		const staffBookings = dayData.bookings || [];
		const staffShifts = dayData.shifts || [];
		const staffBlocks = dayData.blocks || [];

		const start = dayjs(`${dayStr}T${startTime}`);

		// 1️⃣ Make sure click is inside a shift
		let shiftEnd = null;

		staffShifts.forEach((s) => {
			const shiftStart = dayjs(`${dayStr}T${s.start}`);
			const shiftFinish = dayjs(`${dayStr}T${s.end}`);

			if (start.valueOf() >= shiftStart.valueOf() && start.valueOf() < shiftFinish.valueOf()) {
				shiftEnd = shiftFinish;
			}
		});

		if (!shiftEnd) return [];

		// 2️⃣ Block if inside an existing booking
		const insideBooking = staffBookings.some((b) => {
			const bStart = dayjs(b.start);
			const bEnd = dayjs(b.end);

			return start.valueOf() >= bStart.valueOf() && start.valueOf() < bEnd.valueOf();
		});

		if (insideBooking) return [];

		// 3️⃣ Block if inside exclusion / block
		const insideBlock = staffBlocks.some((block) => {
			const blockStart = dayjs(`${dayStr}T${block.start}`);
			const blockEnd = dayjs(`${dayStr}T${block.end}`);

			return start.valueOf() >= blockStart.valueOf() && start.valueOf() < blockEnd.valueOf();
		});

		if (insideBlock) return [];

		// 4️⃣ Find next booking after this time
		const nextBooking = staffBookings
			.map((b) => ({
				start: dayjs(b.start),
			}))
			.filter((b) => b.start.valueOf() > start.valueOf())
			.sort((a, b) => a.start.valueOf() - b.start.valueOf())[0];

		const maxEnd = nextBooking ? nextBooking.start : shiftEnd;

		// 5️⃣ Filter services that fit fully
		return services.filter((svc) => {
			const duration = svc.durationMinutes || svc.duration || 0;

			const proposedEnd = start.add(duration, "minute");

			return proposedEnd.valueOf() <= maxEnd.valueOf();
		});
	};

	function formatTimeDisplay(time) {
		if (!time) return "";

		const d = dayjs(time);

		if (d.isValid()) {
			return d.format("h:mm A");
		}

		// fallback for "HH:mm"
		const [h, m] = time.split(":").map(Number);
		const hour12 = h % 12 || 12;
		const minutes = String(m).padStart(2, "0");
		const period = h >= 12 ? "PM" : "AM";

		return `${hour12}:${minutes} ${period}`;
	}

	const isSlotClickable = (dayStr, startTime) => {
		const dayData = weekData?.week?.[dayStr];
		if (!dayData) return false;

		const start = dayjs(`${dayStr}T${startTime}`);

		// 1️⃣ Must be inside shift
		const insideShift = (dayData.shifts || []).some((s) => {
			const shiftStart = dayjs(`${dayStr}T${s.start}`);
			const shiftEnd = dayjs(`${dayStr}T${s.end}`);
			return start.valueOf() >= shiftStart.valueOf() && start.valueOf() < shiftEnd.valueOf();
		});

		if (!insideShift) return false;

		// 2️⃣ Not inside booking
		const insideBooking = (dayData.bookings || []).some((b) => {
			const bStart = dayjs(b.start);
			const bEnd = dayjs(b.end);
			return start.valueOf() >= bStart.valueOf() && start.valueOf() < bEnd.valueOf();
		});

		if (insideBooking) return false;

		// 3️⃣ Not inside exclusion/block
		const insideBlock = (dayData.blocks || []).some((block) => {
			const blockStart = dayjs(`${dayStr}T${block.start}`);
			const blockEnd = dayjs(`${dayStr}T${block.end}`);
			return start.valueOf() >= blockStart.valueOf() && start.valueOf() < blockEnd.valueOf();
		});

		if (insideBlock) return false;

		return true;
	};

	const handleBlockedClickStaff = (range, dayStr) => {
		switch (range.type) {
			case "exclusion":
				setActiveModal("exclusion");
				break;

			case "outsideShift":
				setActiveModal("baseSchedule");
				break;

			case "break": // 🔥 ADD THIS
				setActiveModal("baseSchedule");
				break;

			case "transit": {
				const booking = weekData?.week?.[dayStr]?.bookings?.find((b) => b.bookingId === range.bookingId);

				if (!booking) return;

				setSelectedEvent({
					...booking,
					date: dayStr,
				});

				setTransitValue(booking.transitMinutes ?? "");
				setEditingTransit(false);
				setShowDetails(true);
				break;
			}

			default:
				break;
		}
	};

	const handleCancelBooking = async () => {
		if (!selectedEvent?.bookingId) return;

		try {
			setSaving(true);

			await companyBasedRequest.cancelBookingStaff(selectedEvent.bookingId, userData.cognitoTokens.AccessToken, role);

			showBanner("success", language === "es" ? "Reserva cancelada" : "Booking cancelled");

			setShowDetails(false);

			await reloadWeek(); // 🔥 refresh calendar
		} catch (err) {
			console.error(err);
			showBanner("error", language === "es" ? "Error al cancelar" : "Failed to cancel booking");
		} finally {
			setSaving(false);
		}
	};

	// const isPastBooking = dayjs(selectedEvent.start).isBefore(dayjs());

	// ---------------- RENDER ----------------
	if (initialLoading) return <CalendarSkeleton />;

	return (
		<div className="sf-card">
			<div className={`sf-layout`}>
				{/* ───────── Sidebar (visible on desktop, slide-in on mobile) ───────── */}

				<main className="sf-main">
					<div className="sf-topbar-modern">
						<button className="sf-today-btn" onClick={handleThisWeek}>
							{translations[language].calendar.toolbar.today}
						</button>

						<div className="sf-date-display-large" onClick={() => setShowDatePicker((prev) => !prev)}>
							{dayjs(weekStart).add(dayIndex, "day").format("dddd, MMM D")}
						</div>

						<div className="sf-menu-wrapper">
							<button className="sf-menu-btn" onClick={() => setShowMenu((prev) => !prev)}>
								{/* <MoreVertical size={22} strokeWidth={2} /> */}
								{translations[language].calendar.editSchedule}
							</button>

							<div className={`sf-dropdown ${showMenu ? "show" : ""}`}>
								<button
									onClick={() => {
										setActiveModal("exclusion");
										setShowMenu(false);
									}}
								>
									{translations[language].calendar.toolbar.blockTime}
								</button>
								<button
									onClick={() => {
										setActiveModal("baseSchedule");
										setShowMenu(false);
									}}
								>
									{translations[language].calendar.toolbar.editSchedule}
								</button>
							</div>
						</div>
					</div>

					<div className="sf-day-strip" onClick={() => setShowDatePicker(true)}>
						<button
							className="sf-week-arrow"
							onClick={() => {
								setWeekStart((w) => dayjs(w).subtract(1, "week"));
								setDayIndex(0);
							}}
						>
							<ChevronLeft />
						</button>
						{days.map((d, idx) => {
							const date = dayjs(weekStart).add(idx, "day");
							const isToday = date.isSame(dayjs(), "day");
							const isActive = view === "day" && selectedDay === d;

							return (
								<div
									className={`sf-day-dot${isToday ? " today" : ""}${isActive ? " active" : ""}`}
									onClick={(e) => {
										e.stopPropagation(); // prevents reopening the calendar
										setView("day");
										setSelectedDay(d);
										setDayIndex(idx);
									}}
								>
									<span className={`dow`}>{d.toUpperCase()}</span>
									<span className={`date-num${isToday ? " today" : ""}`}>{date.format("D")}</span>
								</div>
							);
						})}
						<button
							className="sf-week-arrow"
							onClick={() => {
								setWeekStart((w) => dayjs(w).add(1, "week"));
								setDayIndex(0);
							}}
						>
							<ChevronRight />
						</button>
					</div>

					{showDatePicker && (
						<div className="sf-inline-calendar">
							<div className="sf-calendar-header">
								<button onClick={() => setCalendarMonth((m) => dayjs(m).subtract(1, "month"))}>‹</button>
								<div className="sf-month-label">{calendarMonth.format("MMMM YYYY")}</div>
								<button onClick={() => setCalendarMonth((m) => dayjs(m).add(1, "month"))}>›</button>
							</div>

							<div className="sf-calendar-grid">
								{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
									<div key={d} className="sf-cal-dow">
										{d}
									</div>
								))}

								{/* Render month days */}
								{Array.from({ length: 31 }).map((_, i) => {
									const date = dayjs(calendarMonth).startOf("month").startOf("week").add(i, "day");
									const isCurrent = date.isSame(dayjs(), "day");
									const isSelected = date.format("YYYY-MM-DD") === selectedDateStr;

									return (
										<div
											key={i}
											className={`sf-cal-day ${isSelected ? "selected" : ""} ${isCurrent ? "today" : ""}`}
											onClick={() => handleChooseDate(date.format("YYYY-MM-DD"))}
										>
											{date.date()}
										</div>
									);
								})}
							</div>
						</div>
					)}

					{/* ───────── Calendar Grid ───────── */}
					{/* ───────── Admin Multi-Staff Calendar ───────── */}

					<div className="sf-calendar">
						<div className="sf-hours-and-staff">
							{weekLoading && <MiniCalendarSkeleton />}

							{/* Hours column */}
							<div className="sf-hours">
								{hours.map((h) => (
									<div key={h} className="sf-hour-label">
										{formatTimeDisplay(`${h}:00`)}
									</div>
								))}
							</div>

							{/* Staff Column (single staff — employee mode) */}
							<div className="sf-staff-columns-not-admin">
								{(() => {
									const dayStr = dayjs(weekStart).add(dayIndex, "day").format("YYYY-MM-DD");
									const dayData = weekData?.week?.[dayStr];

									const hasShifts = dayData?.shifts?.length > 0;
									const hasBookings = dayData?.bookings?.length > 0;
									const hasBlocks = dayData?.blocks?.length > 0;
									const hasAnything = hasShifts || hasBookings || hasBlocks;

									// 🔥 EMPTY-STATE — No schedule for the day
									if (!hasAnything) {
										return (
											<div className="sf-empty-state">
												<p>{translations[language].calendar.empty.noScheduleToday}</p>
												<p className="sf-subtle">{translations[language].calendar.empty.notWorking}</p>
											</div>
										);
									}

									// 🔥 STAFF WORKING — render normal calendar
									const bookings = dayData.bookings || [];
									const shifts = dayData.shifts || [];
									const firstHour = hours[0] || 9;

									return (
										<div className="sf-staff-column">
											{/* Staff header */}
											<div className="sf-staff-header">
												{userData.user.photo ? (
													<img className="sf-staff-photo" src={userData.user.photo} alt={userData.user.firstName || "Staff"} />
												) : (
													<div className="sf-staff-placeholder">
														<span>{userData.user.firstName?.charAt(0)}</span>
													</div>
												)}
												<div className="sf-staff-name-calendar">
													{userData.user.firstName} {userData.user.lastName}
												</div>
											</div>

											{/* Hour grid */}
											<div className="sf-hour-grid">
												{/* Hour blocks */}
												{hours.map((h) => (
													<div
														key={h}
														className="sf-hour-block"
														onClick={(e) => {
															const gridEl = e.currentTarget.parentElement;
															if (!gridEl) return;

															const gridRect = gridEl.getBoundingClientRect();
															const clickY = e.clientY - gridRect.top + gridEl.scrollTop;

															const minutesFromTop = (clickY / hourHeight) * 60;

															// ⏱ snap to 30 min
															const snappedMinutes = Math.round(minutesFromTop / 15) * 15;

															const startHour = hours[0];
															const totalMinutes = startHour * 60 + snappedMinutes;

															const hh = Math.floor(totalMinutes / 60);
															const mm = totalMinutes % 60;

															const startTime = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;

															const validServices = getValidServicesForSlot(selectedDateStr, startTime);

															if (!isSlotClickable(selectedDateStr, startTime)) {
																return;
															}

															if (!validServices.length) {
																showBanner(
																	"error",
																	language === "es"
																		? "No hay espacio disponible en ese horario"
																		: "No available time at this slot",
																);
																return;
															}

															setQuickSlot({
																date: selectedDateStr,
																startTime,
																staff: {
																	staffId: userData.user.userId,
																	scheduleId: userData.user.scheduleId,
																	services: userData.user.services,
																},
																validServices,
															});
														}}
													/>
												))}

												{/* Render SHIFTS (background) */}
												{shifts.map((s, i) => {
													const startMin = getMinutes(s.start);
													const endMin = getMinutes(s.end);

													const top = ((startMin - firstHour * 60) / 60) * hourHeight;
													const height = ((endMin - startMin) / 60) * hourHeight;

													return (
														<div
															key={`shift-${i}`}
															className="sf-shift-block"
															style={{
																top,
																height,
																background: "rgba(0, 150, 255, 0.12)",
															}}
														></div>
													);
												})}

												{/* Render BOOKINGS */}
												{bookings.map((b, i) => {
													const startMin = getMinutes(b.start);
													const endMin = getMinutes(b.end);

													const top = ((startMin - firstHour * 60) / 60) * hourHeight;
													const height = ((endMin - startMin) / 60) * hourHeight;

													return (
														<div
															key={i}
															className="sf-event"
															style={{
																top,
																height,
																background: "hsl(210, 80%, 90%)",
															}}
															onClick={() => handleEventClick(b)}
														>
															<div className="sf-event-time-badge">
																{formatTimeDisplay(b.start)} – {formatTimeDisplay(b.end)}
															</div>

															<div className="sf-event-info">
																<div className="sf-event-line">
																	<span className="sf-event-client">{b.clientName || "Booking"}</span>

																	{b.services && (
																		<>
																			<span className="sf-event-separator"> — </span>
																			<span className="sf-event-services-inline">
																				{Array.isArray(b.services)
																					? b.services.map((s) => s.serviceNameEn || s.serviceNameEs).join(", ")
																					: b.services}
																			</span>
																		</>
																	)}
																</div>
															</div>
														</div>
													);
												})}

												{buildInactiveRangesStaff(dayData, hours).map((r, i) => {
													const gridHeight = hours.length * hourHeight;
													const globalStart = hours[0];

													const top = (r.startHour - globalStart) * hourHeight;
													const height = (r.endHour - r.startHour) * hourHeight;

													const clampedTop = Math.max(0, top);
													const clampedHeight = Math.min(height, gridHeight - clampedTop);

													if (clampedHeight <= 0) return null;

													return (
														<div
															key={`inactive-${i}`}
															className="sf-inactive-block sf-clickable-block"
															style={{
																top: clampedTop,
																height: clampedHeight,
															}}
															onClick={(e) => {
																e.stopPropagation();
																handleBlockedClickStaff(r, dayStr);
															}}
														/>
													);
												})}

												{quickSlot && (
													<AddBookingPopup
														date={quickSlot.date}
														startTime={quickSlot.startTime}
														staff={quickSlot.staff}
														services={quickSlot?.validServices || []}
														token={userData.cognitoTokens.AccessToken}
														language={language}
														onClose={() => setQuickSlot(null)}
														onSuccess={() => {
															setQuickSlot(null);
															reloadWeek();
														}}
													/>
												)}
											</div>
										</div>
									);
								})()}
							</div>
						</div>
					</div>

					{/* <button className="sf-fab" onClick={() => setActiveModal("walkin")}>
                        {translations[language].calendar.addWalkIn}
                    </button> */}
				</main>
			</div>

			{/* Popup: Add Personal Event */}
			{showPopup && (
				<div className="sf-popup-overlay">
					<div className="sf-popup">
						<h3>{translations[language].calendar.popup.addEventTitle}</h3>

						<div className="sf-popup-section">
							<label>{translations[language].calendar.popup.title}</label>
							<input
								type="text"
								value={popupData.title}
								onChange={(e) => setPopupData({ ...popupData, title: e.target.value })}
								placeholder={translations[language].calendar.popup.placeholderTitle}
								className="sf-input"
							/>
						</div>

						<div className="sf-popup-section sf-time-row">
							<div className="sf-time-field">
								<label>{translations[language].calendar.popup.start}</label>
								<input
									type="time"
									value={popupData.start}
									onChange={(e) => setPopupData({ ...popupData, start: e.target.value })}
									className="sf-input"
								/>
							</div>

							<div className="sf-time-field">
								<label>{translations[language].calendar.popup.end}</label>
								<input
									type="time"
									value={popupData.end}
									onChange={(e) => setPopupData({ ...popupData, end: e.target.value })}
									className="sf-input"
								/>
							</div>
						</div>

						<div className="sf-popup-section">
							<label>{translations[language].calendar.popup.description}</label>
							<textarea
								value={popupData.desc}
								onChange={(e) => setPopupData({ ...popupData, desc: e.target.value })}
								placeholder={translations[language].calendar.popup.placeholderDesc}
								className="sf-textarea"
							/>
						</div>

						<div className="sf-popup-actions">
							<button className="btn ghost" onClick={() => setShowPopup(false)}>
								{translations[language].calendar.popup.cancel}
							</button>
							<button className="btn" onClick={addPersonalEvent}>
								{translations[language].calendar.popup.addEvent}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Popup: Event Details */}
			{showDetails && selectedEvent && (
				<div className="sf-popup-overlay">
					<div className="sf-popup">
						{/* ── HEADER ── */}
						<div className="sf-cal-detail-header">
							<div className="sf-cal-detail-name">
								{userData.user.firstName} {userData.user.lastName}
							</div>
							<div className="sf-detail-time-chip">
								{formatTimeDisplay(selectedEvent.start)} – {formatTimeDisplay(selectedEvent.end)}
							</div>
						</div>

						{/* ── CLIENT INFO ── */}
						<div className="sf-cal-detail-client-card">
							<div className="sf-cal-detail-client-name">{selectedEvent.clientName || selectedEvent.title || "Booking"}</div>
							{selectedEvent.customerEmail && (
								<div className="sf-cal-detail-client-row">
									<span>✉</span>
									<span>{selectedEvent.customerEmail}</span>
								</div>
							)}
							{selectedEvent.phone && (
								<div className="sf-cal-detail-client-row">
									<Phone size={13} />
									<span>{selectedEvent.phone}</span>
								</div>
							)}
						</div>

						{/* ── SERVICE TYPE BADGE ── */}
						{selectedEvent.isHomeService !== undefined && (
							<div className="sf-cal-detail-type-badge">
								{selectedEvent.isHomeService
									? language === "es"
										? "🏠 Servicio a domicilio"
										: "🏠 Home service"
									: language === "es"
										? "🏪 En el local"
										: "🏪 In-shop"}
							</div>
						)}

						{/* ── SERVICES ── */}
						{(selectedEvent.services || selectedEvent.service)?.length > 0 && (
							<div className="sf-detail-section">
								<div className="sf-section-label">{translations[language].calendar.details.services}</div>
								<div className="sf-service-list">
									{(selectedEvent.services || selectedEvent.service).map((s, idx) => (
										<div key={idx} className="sf-service-pill">
											{s.serviceNameEn || s.serviceNameEs || s.name || "Service"}
										</div>
									))}
								</div>
							</div>
						)}

						{/* ── HOME SERVICE DETAILS ── */}
						{selectedEvent.isHomeService && (
							<div className="sf-location-card">
								<div className="sf-location-address">
									{selectedEvent.address}
									{selectedEvent.city && `, ${selectedEvent.city}`}
									{selectedEvent.postalCode && ` ${selectedEvent.postalCode}`}
								</div>

								{selectedEvent.phone && (
									<div className="sf-location-phone">
										<Phone size={14} />
										{selectedEvent.phone}
									</div>
								)}

								<div className="sf-transit-row">
									{editingTransit ? (
										<>
											<input
												type="number"
												min="0"
												className="sf-transit-input"
												value={transitValue}
												onChange={(e) => setTransitValue(e.target.value)}
											/>
											<span>min</span>
											<button className="sf-inline-save" onClick={saveTransitTime} disabled={transitSaving}>
												{language === "es" ? "Guardar" : "Save"}
											</button>
											<button
												className="sf-inline-cancel"
												onClick={() => {
													setEditingTransit(false);
													setTransitValue(selectedEvent.transitMinutes ?? "");
												}}
											>
												{language === "es" ? "Cancelar" : "Cancel"}
											</button>
										</>
									) : (
										<>
											<span>
												{language === "es"
													? `${selectedEvent.transitMinutes || 0} min de traslado`
													: `${selectedEvent.transitMinutes || 0} min travel buffer`}
											</span>
											<button className="sf-transit-edit-btn" onClick={() => setEditingTransit(true)}>
												<Edit size={14} />
											</button>
										</>
									)}
								</div>
							</div>
						)}

						{/* ── ACTIONS ── */}
						<div className="sf-popup-actions">
							<button className="btn ghost" onClick={() => setShowDetails(false)}>
								{translations[language].calendar.details.close}
							</button>
							<button className="btn danger" onClick={() => setConfirmCancelOpen(true)}>
								{translations[language].calendar.details.cancelBooking}
							</button>
						</div>
					</div>
				</div>
			)}

			{confirmCancelOpen && (
				<div className="sf-confirm-overlay">
					<div className="sf-confirm-modal">
						<h3>{translations[language].calendar.details.confirmCancelTitle}</h3>
						<p className="sf-confirm-text">{translations[language].calendar.details.confirmCancelText}</p>
						<div className="sf-confirm-actions">
							<button className="sf-confirm-btn ghost" onClick={() => setConfirmCancelOpen(false)}>
								{translations[language].calendar.details.confirmCancelNo}
							</button>
							<button
								className="sf-confirm-btn danger"
								onClick={async () => {
									await handleCancelBooking();
									setConfirmCancelOpen(false);
								}}
							>
								{translations[language].calendar.details.confirmCancelYes}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Modals */}
			{activeModal === "baseSchedule" && (
				<BaseScheduleModal
					onClose={() => {
						setActiveModal(null);
						reloadWeek(); // 🔥 force refresh calendar
					}}
					staffList={[
						{
							staffId: userData.user.staffId,
							scheduleId: userData.user.scheduleId,
							week: weekData?.week,
						},
					]}
					token={userData.cognitoTokens.AccessToken}
				/>
			)}

			{activeModal === "exclusion" && (
				<ExclusionModal
					open={true}
					onClose={() => setActiveModal(null)}
					onSaved={reloadWeek}
					staffList={[
						{
							staffId: userData.user.staffId,
							scheduleId: userData.user.scheduleId,
							firstName: userData.user.firstName,
							lastName: userData.user.lastName,
							photo: userData.user.photo,
						},
					]}
					token={userData.cognitoTokens.AccessToken}
					scheduleData={[
						{
							staffId: userData.user.staffId,
							scheduleId: userData.user.scheduleId,
							exclusions: weekData?.exclusions || [],
							bookingsByDate: Object.entries(weekData?.week || {}).reduce((acc, [dateStr, dayObj]) => {
								const bookings = (dayObj.bookings || []).map((b) => ({
									bookingId: b.bookingId,
									staffId: userData.user.staffId,
									dateStr,
									clientName: b.clientName,
									start: b.start,
									end: b.end,
									services: b.service || b.services || [],
									isHomeService: b.isHomeService,
									transitMinutes: b.transitMinutes,
								}));

								if (bookings.length) {
									acc[dateStr] = bookings;
								}

								return acc;
							}, {}),
						},
					]}
					onManageBooking={({ bookingId, dateStr }) => {
						const booking = weekData?.week?.[dateStr]?.bookings?.find((b) => b.bookingId === bookingId);

						if (!booking) return;

						setActiveModal(null);

						setSelectedEvent({
							...booking,
							date: dateStr,
						});

						setTransitValue(booking.transitMinutes ?? "");
						setEditingTransit(false);
						setShowDetails(true);
					}}
				/>
			)}
			{activeModal === "walkin" && (
				<WalkinModal
					onClose={() => {
						setActiveModal(null);
						setWeekStart((prev) => prev.clone()); // 🔥 reload schedule
					}}
					staffList={[
						{
							staffId: userData.user.staffId,
							scheduleId: userData.user.scheduleId,
							week: weekData?.week,
							services: userData.user.services,
						},
					]}
					services={services}
					token={userData.cognitoTokens.AccessToken}
					week={{
						staff: [
							{
								staffId: userData.user.staffId,
								week: weekData?.week,
								services: userData.user.services,
							},
						],
					}}
					selectedDate={selectedDateStr}
				/>
			)}
			{banner && <BannerMessage type={banner.type} text={banner.text} exiting={exiting} />}
			{showConfetti && <Confetti />}
		</div>
	);
};

export default StaffCalendar;
